import * as React from 'react';

import { useSlug } from '../hooks/useSlug';
import { GlobalProps } from '../types';
import { classes, debounce } from '../utils';

import styles from './styles.module.css';

const DEFAULT_SCROLL_SIZE = 200;
const RESIZE_FREQUENCY_IN_MS = 250;

export const findCurrentSnapIndex = (
  current: number,
  snapPositions: number[]
) => {
  let currentSnapIndex = 0;
  let minDifference = -1;

  for (let i = 0; i <= snapPositions.length; i++) {
    const position = snapPositions[i] as number;
    const difference = Math.abs(current - position);

    if (minDifference === -1 || difference <= minDifference) {
      minDifference = difference;
      currentSnapIndex = i;
    }

    if (difference > minDifference) {
      return currentSnapIndex;
    }
  }

  return currentSnapIndex;
};

export const findNextSnapPosition = (
  current: number,
  snapPositions: number[],
  numPositions: number
) => {
  const currentIndex = findCurrentSnapIndex(current, snapPositions);

  if (currentIndex + numPositions >= snapPositions.length) {
    return snapPositions[snapPositions.length - 1];
  }

  return snapPositions[currentIndex + numPositions];
};

export const findPreviousSnapPosition = (
  current: number,
  snapPositions: number[],
  numPositions: number
) => {
  const currentIndex = findCurrentSnapIndex(current, snapPositions);

  if (currentIndex - numPositions < 0) {
    return 0;
  }

  return snapPositions[currentIndex - numPositions];
};

export type SliderProps = GlobalProps & {
  children?: React.ReactNode;
  scrollSize?: number;
  trackClick?: (eventAction: string, eventLabel: string) => void;
  shadow?: boolean;
  boxShadow?: boolean;
  onSliderScroll?: (
    scrollLeft: number,
    isAtStart: boolean,
    isAtEnd: boolean
  ) => void;
  snapPositions?: number[];
  trackRef?: React.MutableRefObject<HTMLDivElement | null>;
};

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
  function SliderComponent(props, ref) {
    const {
      children,
      scrollSize = DEFAULT_SCROLL_SIZE,
      trackClick,
      name = 'slider',
      shadow = true,
      boxShadow = false,
      onSliderScroll,
      snapPositions,
      trackRef = null,
    } = props;

    const slugName = useSlug(name);

    const ownTrackRef = React.useRef<HTMLDivElement | null>(null);
    const track = trackRef || ownTrackRef;
    const [isAtStart, setIsAtStart] = React.useState(true);
    const [isAtEnd, setIsAtEnd] = React.useState(false);

    const onScrollHook = React.useCallback(
      (readOnly?: boolean) => {
        if (track.current) {
          const atEnd =
            track.current.scrollWidth <=
            track.current.offsetWidth + Math.round(track.current.scrollLeft);
          const atStart = track.current.scrollLeft === 0;

          setIsAtEnd(atEnd);
          setIsAtStart(atStart);

          if (onSliderScroll && !readOnly) {
            onSliderScroll(track.current.scrollLeft, atStart, atEnd);
          }
        }
      },
      [setIsAtStart, setIsAtEnd]
    );

    const onScroll = React.useCallback(() => {
      onScrollHook();
    }, [onScrollHook]);

    React.useEffect(() => {
      const onResize = debounce(onScroll, RESIZE_FREQUENCY_IN_MS);

      window.addEventListener('resize', onResize, { passive: true });

      return () => {
        window.removeEventListener('resize', onResize);
      };
    }, []);

    const pageBack = React.useCallback(() => {
      if (track.current) {
        if (trackClick) {
          trackClick('arrow-click', 'arrow-back');
        }

        if (snapPositions && snapPositions.length > 0) {
          const current = track.current.scrollLeft;
          const previous = findPreviousSnapPosition(current, snapPositions, 2);

          if (previous || previous === 0) {
            track.current.scrollTo({ left: previous, behavior: 'smooth' });
          }

          return;
        }

        if (track.current.scrollBy) {
          track.current.scrollBy({ left: -scrollSize, behavior: 'smooth' });
        } else {
          track.current.scrollLeft -= scrollSize;
        }
      }
    }, [trackClick, scrollSize, snapPositions]);

    const pageForward = React.useCallback(() => {
      if (track.current) {
        if (trackClick) {
          trackClick('arrow-click', 'arrow-forward');
        }

        if (snapPositions && snapPositions.length > 0) {
          const current = track.current.scrollLeft;
          const next = findNextSnapPosition(current, snapPositions, 2);

          if (next) {
            track.current.scrollTo({ left: next, behavior: 'smooth' });
          }

          return;
        }

        if (track.current.scrollBy) {
          track.current.scrollBy({ left: scrollSize, behavior: 'smooth' });
        } else {
          track.current.scrollLeft += scrollSize;
        }
      }
    }, [trackClick, scrollSize, snapPositions]);

    React.useEffect(() => {
      onScrollHook(true);
    }, [onScrollHook, children]);

    return (
      <div
        ref={ref}
        className={classes(
          [
            styles.slider,
            isAtStart && styles.isAtStart,
            isAtEnd && styles.isAtEnd,
            boxShadow && styles.boxShadow,
          ],
          props
        )}
        data-component={slugName}
      >
        <div
          className={classes([styles.shadowWrapper, shadow && styles.shadows])}
        >
          <div
            data-component={`${slugName}-track`}
            onScroll={onScroll}
            ref={track}
            className={styles.track}
          >
            {children}
          </div>
        </div>
        <div
          data-component={`${slugName}-navigation`}
          className={styles.navigation}
        >
          <button
            className={styles.arrow}
            type="button"
            onClick={pageBack}
            disabled={isAtStart}
          >
            Back
          </button>
          <button
            className={styles.arrow}
            type="button"
            onClick={pageForward}
            disabled={isAtEnd}
          >
            Forward
          </button>
        </div>
      </div>
    );
  }
);
